// useViewingRequest.jsx


import { getDatabase, ref, get, update } from "firebase/database";
import { useAuth } from "./useAuth";

const useViewingRequests = () => {
  const { currentUser } = useAuth();
  const database = getDatabase();

  // Fetch all viewing requests along with listing addresses and images
  const fetchViewingRequests = async () => {
    if (!currentUser) {
      throw new Error("User is not authenticated.");
    }

    try {
      // Step 1: Get the user's listings
      const listingsRef = ref(database, `users/${currentUser.uid}/listings`);
      const listingsSnapshot = await get(listingsRef);

      if (!listingsSnapshot.exists()) {
        return [];
      }

      const listings = listingsSnapshot.val();
      const viewingRequests = [];

      // Step 2: Fetch viewing requests for each listing, including address & image from listing
      const listingIds = Object.keys(listings);
      for (const listingId of listingIds) {
        const listingDataRef = ref(database, `listings/${listingId}`);
        const listingDataSnapshot = await get(listingDataRef);

        if (!listingDataSnapshot.exists()) {
          continue;
        }

        const listingData = listingDataSnapshot.val();
        const listingAddress = listingData.address || "Address not available";
        const listingCity = listingData.city || "City not available";
        const listingState = listingData.state || "State not available";
        const listingImages = Array.isArray(listingData.images) ? listingData.images : [];
        const listingImage = listingImages.length > 0 ? listingImages[0] : "default-image-url.jpg"; // First image or fallback

        const viewingRequestsRef = ref(database, `listings/${listingId}/viewingRequests`);
        const requestsSnapshot = await get(viewingRequestsRef);

        if (requestsSnapshot.exists()) {
          const requests = Object.entries(requestsSnapshot.val()).map(([id, request]) => ({
            id,
            listingId,
            address: listingAddress, // Attach the listing's address
            city: listingCity,
            state: listingState,
            image: listingImage, // Attach the first listing's image URL
            ...request,
          }));
          viewingRequests.push(...requests);
        }
      }

      return viewingRequests;
    } catch (error) {
      console.error("Error fetching viewing requests:", error);
      throw error;
    }
  };

  // Approve or deny a viewing request
  const updateViewingRequestStatus = async (listingId, requestId, status) => {
    if (!currentUser) {
      throw new Error("User is not authenticated.");
    }

    try {
      const requestRef = ref(database, `listings/${listingId}/viewingRequests/${requestId}`);
      const snapshot = await get(requestRef);

      if (!snapshot.exists()) {
        throw new Error("Viewing request does not exist.");
      }

      await update(requestRef, { status });
      console.log(`Request ${requestId} updated to ${status}`);
    } catch (error) {
      console.error("Error updating request status:", error);
      throw error;
    }
  };

  /** Generic partial update for one viewing-request */
  const patchViewingRequest = async (listingId, requestId, updates) => {
    if (!currentUser) throw new Error("User is not authenticated.");
    const requestRef = ref(
      database,
      `listings/${listingId}/viewingRequests/${requestId}`
    );
    await update(requestRef, updates);
  };

  return { fetchViewingRequests, updateViewingRequestStatus, patchViewingRequest };
};

export default useViewingRequests;
